import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Wrench,
  Phone,
  Shield,
  Star,
  Compass,
  ArrowRight,
  Sparkles,
  MapPin,
  Clock,
  Mail,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  Navigation,
  ShieldAlert,
  Car,
  MessageSquare
} from "lucide-react";
import { servicesData, reviewsData, galleryData, submitContactInquiry } from "@/mock";

export default function Home() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: "mobile-mechanic-service",
    isEmergency: "no",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error'
  const [activeFaq, setActiveFaq] = useState(null);
  const [showHeroPhoneDropdown, setShowHeroPhoneDropdown] = useState(false);
  const location = useLocation();

  // Scroll to section based on hash & inject LocalBusiness Schema for Google Local SEO
  useEffect(() => {
    // 1. Scroll logic
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    // 2. Inject Google Local Business Schema (Legendary Local SEO Spark)
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "AutoRepair",
      "name": "KG Auto Repair",
      "image": "https://static.wixstatic.com/media/9da336_1c1a8308e9ff4f5cbfd0ecbafbcfd890~mv2.jpeg",
      "@id": "https://www.kgautolp.com/",
      "url": "https://www.kgautolp.com/",
      "telephone": "304-685-3724",
      "priceRange": "$$",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "481 Indian Rock Road",
        "addressLocality": "Reedsville",
        "addressRegion": "WV",
        "postalCode": "26547",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 39.5162,
        "longitude": -79.7997
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "09:00",
          "closes": "18:00"
        }
      ],
      "areaServed": [
        { "@type": "AdministrativeArea", "name": "Morgantown, WV" },
        { "@type": "AdministrativeArea", "name": "Fairmont, WV" },
        { "@type": "AdministrativeArea", "name": "Preston County, WV" },
        { "@type": "AdministrativeArea", "name": "Kingwood, WV" },
        { "@type": "AdministrativeArea", "name": "Clarksburg, WV" }
      ],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "5.0",
        "reviewCount": "24"
      }
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "local-seo-schema";
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById("local-seo-schema");
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [location]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.message) {
      setSubmitStatus("error");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      await submitContactInquiry(formData);
      setSubmitStatus("success");
      setFormData({
        name: "",
        phone: "",
        email: "",
        service: "mobile-mechanic-service",
        isEmergency: "no",
        message: ""
      });
    } catch (err) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const localFaqData = [
    {
      q: "How does your mobile mechanic service work?",
      a: "It's simple and stress-free! Instead of paying for an expensive tow truck and waiting in a shop lobby, our professional mobile unit drives directly to your home, office, or roadside. We bring advanced diagnostic scanners, fluids, and parts to fix your car on-site."
    },
    {
      q: "Do you charge extra fees or travel charges for mobile service?",
      a: "No! We combine transparent, fair pricing with no hidden charges. Our mobile dispatch service fee is extremely reasonable and often far less than the cost of a flatbed tow to a traditional dealership."
    },
    {
      q: "What if my repair is too complex to be done on-site?",
      a: "While we complete over 90% of repairs on-site (brakes, starters, batteries, AC, light electrical), we own a fully-equipped dedicated garage at 481 Indian Rock Road in Reedsville, WV to handle heavy-duty engine rebuilds, timing kits, and complex transmission swaps safely."
    },
    {
      q: "What European brands do you specialize in?",
      a: "KG Auto specializes in specialized European car repair. We have factory-level diagnostics and custom tools for BMW, Mini Cooper, Audi, Volkswagen, Volvo, and Mercedes-Benz, providing dealer-level care at local, budget-friendly rates."
    }
  ];

  return (
    <div className="bg-zinc-950 text-zinc-100 min-h-screen">
      {/* 1. HERO SECTION (Redesigned for Dual Focus: Mobile + Premium In-Shop) */}
      <section className="relative min-h-[80vh] sm:min-h-[95vh] flex items-center justify-center py-10 sm:py-20 px-4 overflow-hidden border-b border-zinc-900">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7"
            alt="Premium Garage Backdrop"
            className="w-full h-full object-cover object-center opacity-20 filter grayscale"
          />
          <div className="absolute inset-0 bg-radial-gradient from-transparent to-zinc-950/90" />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-transparent" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto text-center flex flex-col items-center">
          {/* Enhanced Localized Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-950/40 border border-blue-500/30 rounded-full text-blue-400 text-xs font-bold tracking-wider mb-6 animate-fade-in">
            <span className="inline-block h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
            <span>📍 MOBILE CONVENIENCE & PREMIUM IN-SHOP EXPERTISE • WV</span>
          </div>

          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-tight max-w-5xl text-white">
            MOBILE CONVENIENCE.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-cyan-400">
              FULL-SERVICE SHOP EXPERTISE.
            </span>
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-zinc-300 max-w-3xl leading-relaxed">
            Get certified mobile mechanics dispatched to your location for quick repairs, or bring your car to our state-of-the-art Reedsville garage for precision engine rebuilding, Euro diagnostics, and complex mechanical overhauls.
          </p>

          {/* Quick trust metrics */}
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 justify-center text-sm font-semibold text-zinc-400">
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-blue-500" /> Fully-Equipped Garage Facility</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-blue-500" /> Emergency Mobile Dispatch</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-blue-500" /> Fair & Honest Pricing</span>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center w-full max-w-lg relative">
            <div className="relative w-full sm:w-auto">
              <button
                onClick={() => setShowHeroPhoneDropdown(!showHeroPhoneDropdown)}
                className="w-full sm:w-auto px-8 py-4 rounded-xl font-bold bg-blue-600 hover:bg-blue-500 text-white transition-all shadow-xl shadow-blue-900/30 text-center flex items-center justify-center gap-2 focus:outline-none"
              >
                <Phone className="h-5 w-5" />
                <span>Call or Text</span>
                <ChevronDown className={`h-4 w-4 transform transition-transform duration-150 ${showHeroPhoneDropdown ? "rotate-180" : ""}`} />
              </button>

              {showHeroPhoneDropdown && (
                <div className="absolute left-0 right-0 sm:left-0 sm:w-56 top-16 mt-2 rounded-xl bg-zinc-900 border border-zinc-800 shadow-2xl p-1.5 z-50 flex flex-col">
                  <a
                    href="tel:3046853724"
                    onClick={() => setShowHeroPhoneDropdown(false)}
                    className="flex items-center gap-2.5 p-3.5 rounded-lg text-sm font-bold text-zinc-100 hover:bg-zinc-800 hover:text-white transition-colors"
                  >
                    <Phone className="h-4 w-4 text-blue-500" />
                    <span>Call 304-685-3724</span>
                  </a>
                  <a
                    href="sms:3046853724"
                    onClick={() => setShowHeroPhoneDropdown(false)}
                    className="flex items-center gap-2.5 p-3.5 rounded-lg text-sm font-bold text-zinc-100 hover:bg-zinc-800 hover:text-white transition-colors border-t border-zinc-850"
                  >
                    <MessageSquare className="h-4 w-4 text-blue-500" />
                    <span>Text 304-685-3724</span>
                  </a>
                </div>
              )}
            </div>
            <a
              href="#contact"
              className="w-full sm:w-auto px-8 py-4 rounded-xl font-bold bg-zinc-900 hover:bg-zinc-850 text-zinc-200 border border-zinc-800 transition-all text-center"
            >
              Request A Quote
            </a>
          </div>

          {/* Redesigned Clean Trust Indicators (Replaced Confusing Text) */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 w-full max-w-4xl pt-8 border-t border-zinc-900">
            <div className="flex flex-col items-center text-center">
              <span className="text-2xl font-extrabold text-white">Full Shop</span>
              <span className="text-xs text-zinc-500 mt-1 uppercase tracking-wider font-bold">Reedsville Garage</span>
            </div>
            <div className="flex flex-col items-center text-center">
              <span className="text-2xl font-extrabold text-blue-500">55+ Mi</span>
              <span className="text-xs text-zinc-500 mt-1 uppercase tracking-wider font-bold">Mobile Radius</span>
            </div>
            <div className="flex flex-col items-center text-center">
              <span className="text-2xl font-extrabold text-white">Euro Spec</span>
              <span className="text-xs text-zinc-500 mt-1 uppercase tracking-wider font-bold">BMW / VW / Audi</span>
            </div>
            <div className="flex flex-col items-center text-center">
              <span className="text-2xl font-extrabold text-blue-500">5.0 Star</span>
              <span className="text-xs text-zinc-500 mt-1 uppercase tracking-wider font-bold">Google Rated</span>
            </div>
          </div>
        </div>
      </section>

      {/* NEW SECTION: COMPARATIVE SERVICES (IN-SHOP VS MOBILE) */}
      <section className="py-24 bg-zinc-900/20 border-b border-zinc-900 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold text-blue-500 uppercase tracking-widest font-extrabold">HOW WE WORK</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-2">
              Two Ways to Service Your Vehicle
            </h2>
            <p className="mt-4 text-zinc-400 text-sm">
              We are not just mobile mechanics on the road. We combine ultimate on-site convenience with a state-of-the-art dedicated repair facility to handle any size job.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {/* Box 1: Full-Equipped Reedsville Garage */}
            <div className="bg-zinc-900 border border-zinc-800 p-8 rounded-3xl flex flex-col justify-between hover:border-blue-500/25 hover:shadow-2xl hover:shadow-blue-950/5 transition-all">
              <div>
                <div className="inline-flex items-center justify-center p-3.5 bg-blue-600 rounded-2xl mb-6 shadow-lg shadow-blue-900/10">
                  <Wrench className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-2xl font-extrabold text-white">In-Shop Repairs (Reedsville Garage)</h3>
                <p className="text-sm text-blue-400 font-semibold mt-1">481 Indian Rock Road, Reedsville WV</p>
                <p className="text-zinc-400 text-sm mt-4 leading-relaxed">
                  Our professional, fully-equipped garage is designed to handle major diagnostics, heavy clean-room repair work, and specialized mechanical jobs that require structural lifts, absolute fluid containment, and heavy shop instrumentation.
                </p>
                
                <ul className="mt-6 space-y-3">
                  <li className="flex items-center gap-2.5 text-sm text-zinc-300">
                    <CheckCircle2 className="h-4 w-4 text-blue-500" />
                    <span>Clean-room engine timing overhauls & head rebuilds</span>
                  </li>
                  <li className="flex items-center gap-2.5 text-sm text-zinc-300">
                    <CheckCircle2 className="h-4 w-4 text-blue-500" />
                    <span>Complete engine, motor, and transmission swaps</span>
                  </li>
                  <li className="flex items-center gap-2.5 text-sm text-zinc-300">
                    <CheckCircle2 className="h-4 w-4 text-blue-500" />
                    <span>Advanced European software flashing & adaptations</span>
                  </li>
                  <li className="flex items-center gap-2.5 text-sm text-zinc-300">
                    <CheckCircle2 className="h-4 w-4 text-blue-500" />
                    <span>Heavy suspension overhauls & frame modification</span>
                  </li>
                </ul>
              </div>
              <div className="mt-8 pt-6 border-t border-zinc-800">
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 text-sm font-bold text-blue-500 hover:text-blue-400 transition-colors"
                >
                  <span>Schedule Shop Drop-Off</span>
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>

            {/* Box 2: Emergency Roadside & Fleet Mobile Service */}
            <div className="bg-zinc-900 border border-zinc-800 p-8 rounded-3xl flex flex-col justify-between hover:border-blue-500/25 hover:shadow-2xl hover:shadow-blue-950/5 transition-all">
              <div>
                <div className="inline-flex items-center justify-center p-3.5 bg-zinc-800 rounded-2xl mb-6 border border-zinc-750">
                  <Car className="h-6 w-6 text-blue-500" />
                </div>
                <h3 className="text-2xl font-extrabold text-white">Emergency Roadside & On-Site Mobile</h3>
                <p className="text-sm text-zinc-500 mt-1">Servicing a 55-mile radius around Preston County</p>
                <p className="text-zinc-400 text-sm mt-4 leading-relaxed">
                  Skip the hassle of calling expensive tow trucks. Our fully equipped service vehicles drive directly to your home, commercial facility, or stranded roadside location—including interstate shoulders—to resolve issues on the spot for passenger cars, commercial fleet vehicles, and trucks.
                </p>
                
                <ul className="mt-6 space-y-3">
                  <li className="flex items-center gap-2.5 text-sm text-zinc-300">
                    <CheckCircle2 className="h-4 w-4 text-blue-500" />
                    <span>On-site brake pad, rotor, and commercial fluid service</span>
                  </li>
                  <li className="flex items-center gap-2.5 text-sm text-zinc-300">
                    <CheckCircle2 className="h-4 w-4 text-blue-500" />
                    <span>Heavy-duty starter, alternator, and battery swaps on the spot</span>
                  </li>
                  <li className="flex items-center gap-2.5 text-sm text-zinc-300">
                    <CheckCircle2 className="h-4 w-4 text-blue-500" />
                    <span>Emergency roadside help on interstates & secondary routes</span>
                  </li>
                  <li className="flex items-center gap-2.5 text-sm text-zinc-300">
                    <CheckCircle2 className="h-4 w-4 text-blue-500" />
                    <span>Fleet multi-point diagnostics, sensor scans & preventive care</span>
                  </li>
                </ul>
              </div>
              <div className="mt-8 pt-6 border-t border-zinc-800">
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 text-sm font-bold text-blue-500 hover:text-blue-400 transition-colors"
                >
                  <span>Dispatch Mobile Mechanic</span>
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. SERVICES SECTION */}
      <section id="services" className="py-24 px-4 max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-xs font-bold text-blue-500 uppercase tracking-widest">Our Expertise</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-2">
            Mobile & In-Shop Auto Services
          </h2>
          <p className="mt-4 text-zinc-400">
            Whether you need the absolute convenience of a mobile mechanic at your driveway or high-precision engine rebuild tools inside our specialized Reedsville garage, we have you covered.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {servicesData.map((service) => (
            <div
              key={service.id}
              className="bg-zinc-900/60 border border-zinc-800 rounded-2xl overflow-hidden hover:border-blue-500/30 hover:shadow-2xl hover:shadow-blue-950/10 transition-all duration-350 group flex flex-col h-full"
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent" />
              </div>
              <div className="p-6 flex-grow flex flex-col justify-between">
                <div>
                  <h3 className="text-xl font-bold text-white group-hover:text-blue-400 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-xs font-semibold text-zinc-500 mt-1">
                    {service.subtitle}
                  </p>
                  <p className="text-sm text-zinc-400 mt-4 line-clamp-3 leading-relaxed">
                    {service.summary}
                  </p>
                </div>
                <div className="mt-6 pt-4 border-t border-zinc-800/80 flex items-center justify-between">
                  <Link
                    to={`/services/${service.id}`}
                    className="inline-flex items-center gap-1 text-sm font-bold text-blue-500 hover:text-blue-400 transition-colors"
                  >
                    <span>Read More</span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 3. WHY CHOOSE US / ABOUT SECTION */}
      <section id="about" className="py-24 bg-zinc-900/40 border-y border-zinc-900 px-4">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Text content */}
          <div>
            <span className="text-xs font-bold text-blue-500 uppercase tracking-widest">ABOUT KG AUTO</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-2 leading-tight">
              Family-Owned Auto Care You Can Trust
            </h2>
            <p className="text-zinc-300 mt-6 leading-relaxed">
              At KG AUTO REPAIR, we take pride in being a family-owned and operated business dedicated to providing top-notch care for your vehicle in Preston County, West Virginia.
            </p>
            <p className="text-zinc-400 mt-4 leading-relaxed">
              Whether you prefer the ultimate convenience of our mobile auto repair services or the thoroughness of our in-shop automotive repairs in Reedsville, we offer a wide array of auto services designed to meet your specific needs.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-zinc-300">Direct contact with your mechanic</span>
              </div>
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-zinc-300">European vehicle experts (BMW, VW)</span>
              </div>
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-zinc-300">No heavy shop or dealership markups</span>
              </div>
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-zinc-300">Prompt roadside mobile units</span>
              </div>
            </div>
          </div>

          {/* Visual features layout */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-3xl blur-3xl opacity-10" />
            <div className="relative bg-zinc-900 border border-zinc-800 p-8 rounded-3xl">
              <h3 className="text-xl font-bold text-white mb-6">Our Core Commitment</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-zinc-800 rounded-xl">
                    <Wrench className="h-6 w-6 text-blue-500" />
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-white">Full Range mobile mechanic service</h4>
                    <p className="text-sm text-zinc-400 mt-1">
                      Serving Morgantown, Kingwood, Fairmont, Clarksburg, and more. Let us come to you at home or work!
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-zinc-800 rounded-xl">
                    <Shield className="h-6 w-6 text-blue-500" />
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-white">Certified Engine & Tech Care</h4>
                    <p className="text-sm text-zinc-400 mt-1">
                      Timing, complete engine rebuilds, suspension work, and high-performance custom upgrades.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEW SECTION: SERVICE AREAS (CRITICAL FOR LOCAL GOOGLE ADS & SEO) */}
      <section className="py-20 bg-zinc-950 px-4 border-b border-zinc-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1">
              <span className="text-xs font-bold text-blue-500 uppercase tracking-widest">WV COVERAGE DEPT</span>
              <h2 className="text-3xl font-extrabold text-white mt-2 leading-snug">
                Where We Drive
              </h2>
              <p className="text-zinc-400 mt-4 text-sm leading-relaxed">
                Our mobile auto mechanics carry professional diagnostics directly to your door, spanning a wide 55-mile service radius across West Virginia.
              </p>
              <div className="mt-6 flex items-center gap-2 text-xs font-bold text-blue-400 uppercase tracking-wider">
                <Navigation className="h-4 w-4 text-blue-500 animate-bounce" />
                <span>Immediate Roadside Dispatch</span>
              </div>
            </div>

            <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 p-8 rounded-3xl">
              <h3 className="text-lg font-bold text-white mb-6">Proudly Serving:</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[
                  "Morgantown, WV",
                  "Fairmont, WV",
                  "Reedsville, WV",
                  "Kingwood, WV",
                  "Clarksburg, WV",
                  "Rowlesburg, WV",
                  "Masontown, WV",
                  "Arthurdale, WV",
                  "Preston County"
                ].map((area) => (
                  <div key={area} className="flex items-center gap-2 text-sm text-zinc-300">
                    <div className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                    <span>{area}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-zinc-800 mt-6 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-zinc-500">
                <p>Not sure if you are in our mobile mechanic service zone? Reach out anyway!</p>
                <a href="tel:3046853724" className="text-blue-500 font-bold hover:underline">
                  Check Service Area →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. REVIEWS SECTION */}
      <section className="py-24 px-4 max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-xs font-bold text-blue-500 uppercase tracking-widest">Testimonials</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-2">What Our Customers Say</h2>
          <div className="mt-4 flex items-center justify-center gap-1 text-orange-500">
            <Star className="h-5 w-5 fill-current" />
            <Star className="h-5 w-5 fill-current" />
            <Star className="h-5 w-5 fill-current" />
            <Star className="h-5 w-5 fill-current" />
            <Star className="h-5 w-5 fill-current" />
            <span className="text-zinc-300 font-bold ml-2">5.0 Star Rated on Google</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {reviewsData.map((review) => (
            <div
              key={review.id}
              className="bg-zinc-900 border border-zinc-800/80 p-8 rounded-2xl flex flex-col justify-between hover:border-zinc-700 transition-colors"
            >
              <div>
                <div className="flex items-center gap-1 text-orange-500 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={`star-${review.id}-${i}`} className="h-4 w-4 fill-current" />
                  ))}
                </div>
                <p className="text-zinc-300 italic text-sm sm:text-base leading-relaxed">
                  &ldquo;{review.text}&rdquo;
                </p>
              </div>
              <div className="mt-6 pt-4 border-t border-zinc-850 flex justify-between items-center text-xs">
                <span className="font-bold text-white">{review.author}</span>
                <span className="text-zinc-500">{review.date}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <a
            href="https://g.co/kgs/jwuUWUK"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold bg-zinc-900 border border-zinc-800 text-zinc-300 hover:text-white hover:border-zinc-700 transition-colors"
          >
            Leave a Review on Google
          </a>
        </div>
      </section>

      {/* 5. GALLERY SECTION */}
      <section className="py-24 bg-zinc-900/40 border-t border-zinc-900 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs font-bold text-blue-500 uppercase tracking-widest">Our Work</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-2">Browse Our Repairs</h2>
            <p className="text-zinc-400 mt-2">Real pictures of engines, cylinder heads, and mobile roadside calls.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {galleryData.map((item) => (
              <div
                key={item.id}
                className="group relative rounded-2xl overflow-hidden border border-zinc-800 bg-zinc-950 aspect-[16/9] sm:aspect-[2/1] overflow-hidden"
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-103"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-lg font-bold text-white">{item.title}</h3>
                  <p className="text-xs text-zinc-400 mt-1">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEW SECTION: FAQS ACCORDION (CRITICAL FOR SEO & USER CONFIDENCE) */}
      <section className="py-24 bg-zinc-950 border-t border-zinc-900 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold text-blue-500 uppercase tracking-widest">FAQs</span>
            <h2 className="text-3xl font-extrabold text-white mt-2">Frequently Asked Questions</h2>
            <p className="text-zinc-400 mt-2 text-sm">Everything you need to know about our mobile and garage repair services.</p>
          </div>

          <div className="space-y-4">
            {localFaqData.map((faq, idx) => (
              <div
                key={`faq-${idx}`}
                className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden transition-colors"
              >
                <button
                  onClick={() => toggleFaq(idx)}
                  className="w-full p-6 text-left flex items-center justify-between gap-4 font-bold text-white hover:text-blue-400 transition-colors"
                >
                  <span>{faq.q}</span>
                  <ChevronDown
                    className={`h-5 w-5 text-zinc-500 transform transition-transform duration-200 ${
                      activeFaq === idx ? "rotate-180 text-blue-500" : ""
                    }`}
                  />
                </button>
                {activeFaq === idx && (
                  <div className="px-6 pb-6 text-sm text-zinc-300 leading-relaxed border-t border-zinc-850 pt-4">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. CONTACT & REDESIGNED APPOINTMENT FORM (Optimized with Breakdown Urgency Toggle) */}
      <section id="contact" className="py-24 px-4 max-w-7xl mx-auto border-t border-zinc-900">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Address and info Column */}
          <div>
            <span className="text-xs font-bold text-blue-500 uppercase tracking-widest">Get In Touch</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-2">
              Have An Auto Inquiry?
            </h2>
            <p className="mt-4 text-zinc-400 leading-relaxed max-w-md">
              We are here to assist. Feel free to text, call, or email us. Get quick advice, estimated quotes, or coordinate where to meet our mobile team.
            </p>

            <div className="space-y-6 mt-8">
              <div className="flex gap-4">
                <div className="p-3 bg-zinc-900 border border-zinc-800 rounded-xl flex-shrink-0">
                  <Phone className="h-6 w-6 text-blue-500" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Call or Text</h4>
                  <a
                    href="tel:3046853724"
                    className="text-lg font-extrabold text-white hover:text-blue-500 transition-colors"
                  >
                    304-685-3724
                  </a>
                  <p className="text-xs text-zinc-500 mt-0.5">Talk directly to Grant</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="p-3 bg-zinc-900 border border-zinc-800 rounded-xl flex-shrink-0">
                  <Mail className="h-6 w-6 text-blue-500" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Email Address</h4>
                  <a
                    href="mailto:kgauto304@gmail.com"
                    className="text-lg font-extrabold text-white hover:text-blue-500 transition-colors"
                  >
                    kgauto304@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="p-3 bg-zinc-900 border border-zinc-800 rounded-xl flex-shrink-0">
                  <MapPin className="h-6 w-6 text-blue-500" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Our Shop Location</h4>
                  <a
                    href="https://google.com/maps"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-base font-bold text-white hover:text-blue-500 transition-colors"
                  >
                    481 Indian Rock Road, Reedsville WV, 26547
                  </a>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="p-3 bg-zinc-900 border border-zinc-800 rounded-xl flex-shrink-0">
                  <Clock className="h-6 w-6 text-blue-500" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Availability Hours</h4>
                  <p className="text-sm font-semibold text-zinc-300">Monday - Friday: 9:00 AM - 6:00 PM</p>
                  <p className="text-xs text-zinc-500 mt-0.5">Saturday - Sunday: Call/Text for appointments</p>
                </div>
              </div>
            </div>
          </div>

          {/* Redesigned Urgent lead capturing form Column */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 shadow-2xl relative">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white">Send A Message</h3>
              <div className="flex items-center gap-1 text-xs text-blue-400 font-semibold bg-blue-950/40 border border-blue-500/20 px-2 py-1 rounded">
                <span>⚡ Quick Response Dispatch</span>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 text-sm transition-colors"
                  placeholder="Your Full Name"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 text-sm transition-colors"
                    placeholder="Phone Number"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 text-sm transition-colors"
                    placeholder="email@example.com"
                  />
                </div>
              </div>

              {/* HIGH CONVERTING EMERGENCY ROW */}
              <div className="p-4 bg-zinc-950 border border-zinc-800/80 rounded-xl">
                <span className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3 flex items-center gap-1.5">
                  <ShieldAlert className="h-4 w-4 text-blue-500" /> Is this an emergency roadside breakdown?
                </span>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold">
                    <input
                      type="radio"
                      name="isEmergency"
                      value="yes"
                      checked={formData.isEmergency === "yes"}
                      onChange={handleChange}
                      className="accent-blue-500 h-4 w-4"
                    />
                    <span className="text-zinc-200">Yes, I am stranded!</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold">
                    <input
                      type="radio"
                      name="isEmergency"
                      value="no"
                      checked={formData.isEmergency === "no"}
                      onChange={handleChange}
                      className="accent-blue-500 h-4 w-4"
                    />
                    <span className="text-zinc-400">No, regular service request</span>
                  </label>
                </div>
              </div>

              <div>
                <label htmlFor="service" className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                  Select Requested Service
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 text-sm transition-colors"
                >
                  {servicesData.map((service) => (
                    <option key={service.id} value={service.id} className="bg-zinc-900">
                      {service.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                  Tell us about your vehicle & symptoms *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 text-sm transition-colors resize-none"
                  placeholder="e.g. 2015 BMW 328i, coolant warning light is on..."
                />
              </div>

              {submitStatus === "success" && (
                <div className="p-4 bg-emerald-950/40 border border-emerald-500/30 rounded-xl flex items-start gap-3 text-emerald-400 text-sm animate-fade-in">
                  <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold">Message Sent Successfully!</p>
                    <p className="text-xs text-emerald-500 mt-1">
                      Thank you for contacting KG Auto. Grant will reach out via call/text shortly.
                    </p>
                  </div>
                </div>
              )}

              {submitStatus === "error" && (
                <div className="p-4 bg-rose-950/40 border border-rose-500/30 rounded-xl flex items-start gap-3 text-rose-400 text-sm">
                  <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold">Missing Information</p>
                    <p className="text-xs text-rose-500 mt-1">
                      Please make sure to fill out all required fields marked with * correctly.
                    </p>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-4 px-6 rounded-xl transition-all disabled:opacity-50 text-sm tracking-wide"
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
